import { useQueries, useQuery } from "@tanstack/react-query"
import {
	Archive,
	ArchiveRestore,
	Megaphone,
	Pencil,
	Plus,
	Search,
	Sparkles,
	Trash2,
	Users,
} from "lucide-react"
import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { plural } from "@/entities/audience"
import { getTagStats, statsKeys } from "@/entities/bot-stats"
import { broadcastKeys, countBroadcastRecipients, totalOf } from "@/entities/broadcast"
import { AUDIENCE_PRESETS, useStartBroadcast } from "@/features/send-broadcast"
import { Button } from "@/shared/ui/button"
import { Checkbox } from "@/shared/ui/checkbox"
import { useConfirm } from "@/shared/ui/confirm-provider"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { EmptyState } from "@/shared/ui/empty-state"
import { Input } from "@/shared/ui/input"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table"
import { AnimatedNumber } from "@/shared/ui/animated-number"
import { AutoHeight } from "@/shared/ui/auto-height"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Reveal } from "@/shared/ui/reveal"
import {
	botPath,
	type LoadedDraft,
	useBotWorkspace,
	WithDraft,
	WorkspaceSection,
} from "@/widgets/bot-workspace"

const TITLE = "Группы"
const SUBTITLE =
	"Группы делят людей на «горячих», «купили курс», «пришли с эфира». Человек может быть в нескольких сразу. Автоматические группы считаются сами, свои наполняются блоками воронки и вручную."
const PEOPLE: [string, string, string] = ["человек", "человека", "человек"]
/** Пока в боте не было ни одной оплаты, эти две группы пустая и «все»: толку от них нет. */
const PAYMENT_PRESETS = new Set(["paid", "not-paid"])
/** Одноразовые «снимки»: списки по датам, «кто уйдёт ДД.ММ», «не в клубе» на момент выгрузки. */
const SNAPSHOT_NAME =
	/\d{1,2}\.\d{1,2}(\.\d{2,4})?|кто уйд[ёе]т|кончается подписка|для рассылки|рассылк\w* конкретн|никогда не покупал|не в клубе|не в кмк/i

type View = "active" | "archive"

/** «Аналитика → Группы»: автоматические и свои группы, архив, состав каждой. */
export function GroupsPage() {
	return (
		<WithDraft title={TITLE} subtitle={SUBTITLE}>
			{(state) => <GroupsContent state={state} />}
		</WithDraft>
	)
}

function GroupsContent({ state }: { state: LoadedDraft }) {
	const botId = useBotWorkspace().bot.id
	return (
		<WorkspaceSection title={TITLE} subtitle={SUBTITLE}>
			<div className="space-y-8">
				<AutoGroups botId={botId} />
				<ManualGroups state={state} botId={botId} />
			</div>
		</WorkspaceSection>
	)
}

function AutoGroups({ botId }: { botId: string }) {
	const startBroadcast = useStartBroadcast(botId)
	// Фильтры считаются один раз: дата «новых за неделю» иначе меняла бы ключ запроса.
	const presets = useMemo(
		() => AUDIENCE_PRESETS.map((preset) => ({ ...preset, value: preset.filter() })),
		[],
	)
	const counts = useQueries({
		queries: presets.map((preset) => ({
			queryKey: broadcastKeys.count(botId, preset.value),
			queryFn: () => countBroadcastRecipients(botId, preset.value),
			staleTime: 60_000,
		})),
	})

	const paidIndex = presets.findIndex((preset) => preset.id === "paid")
	const noPaymentsYet = counts[paidIndex]?.data?.count === 0
	const shown = presets
		.map((preset, index) => ({ preset, count: totalOf(counts[index]?.data) }))
		.filter(({ preset }) => !(noPaymentsYet && PAYMENT_PRESETS.has(preset.id)))

	return (
		<section aria-labelledby="auto-groups">
			<div className="mb-3 flex items-center gap-2">
				<Sparkles className="size-4 text-primary" />
				<h2 id="auto-groups" className="text-base font-semibold tracking-tight">
					Автоматические
				</h2>
				<span className="text-sm text-muted-foreground">считаются сами, обновлять не нужно</span>
			</div>
			<ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
				{shown.map(({ preset, count }) => {
					return (
						<li
							key={preset.id}
							className="group/auto relative flex items-center gap-3 rounded-2xl border border-border bg-card p-3 transition-colors hover:bg-accent/40"
						>
							<Link
								to={botPath(botId, `analytics/groups/auto-${preset.id}`)}
								className="min-w-0 flex-1 after:absolute after:inset-0 after:rounded-2xl"
							>
								<span className="block text-sm leading-5 font-medium">{preset.label}</span>
								<span className="block text-xs text-muted-foreground">{preset.hint}</span>
							</Link>
							<span className="relative text-right text-sm tabular-nums text-muted-foreground">
								{count === undefined ? "…" : <AnimatedNumber value={count} />}
							</span>
							<Button
								variant="ghost"
								size="icon"
								className="relative z-10 size-8 shrink-0"
								disabled={startBroadcast.isPending || count === 0}
								title="Рассылка этой группе"
								aria-label={`Рассылка: ${preset.label}`}
								onClick={() => startBroadcast.mutate({ name: preset.label, filter: preset.value })}
							>
								<Megaphone />
							</Button>
						</li>
					)
				})}
			</ul>
		</section>
	)
}

function ManualGroups({ state, botId }: { state: LoadedDraft; botId: string }) {
	const { actions } = state
	const { confirm } = useConfirm()
	const startBroadcast = useStartBroadcast(botId)
	const stats = useQuery({ queryKey: statsKeys.tags(botId), queryFn: () => getTagStats(botId) })
	const counts = new Map(stats.data?.tags.map((row) => [row.tagId, row.count]))
	const [view, setView] = useState<View>("active")
	const [query, setQuery] = useState("")
	const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
	const [editing, setEditing] = useState<{ id?: string; name: string } | null>(null)

	const all = state.draft.tags ?? []
	const archivedCount = all.filter((tag) => tag.archivedAt).length
	const needle = query.trim().toLowerCase()
	const tags = all
		.filter((tag) => (view === "archive" ? !!tag.archivedAt : !tag.archivedAt))
		.filter((tag) => !needle || tag.name.toLowerCase().includes(needle))
		.sort((a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0))
	const snapshots = all.filter((tag) => !tag.archivedAt && SNAPSHOT_NAME.test(tag.name))
	const name = editing?.name.trim() ?? ""
	const duplicate = all.some(
		(tag) => tag.id !== editing?.id && tag.name.toLowerCase() === name.toLowerCase(),
	)
	const pickedIds = tags.filter((tag) => picked.has(tag.id)).map((tag) => tag.id)
	const allPicked = tags.length > 0 && pickedIds.length === tags.length

	const save = async () => {
		if (!editing || !name || duplicate) return
		if (editing.id) await actions.renameTag(editing.id, name)
		else await actions.createTag(name)
		setEditing(null)
	}
	const setArchived = async (ids: string[], archived: boolean) => {
		if (ids.length === 1 && ids[0]) await actions.setTagArchived(ids[0], archived)
		else await actions.archiveTags(ids, archived)
		setPicked(new Set())
	}
	const askArchive = (ids: string[], archived: boolean) =>
		confirm({
			title: archived
				? `Убрать в архив: ${ids.length} ${plural(ids.length, ["группа", "группы", "групп"])}?`
				: `Вернуть из архива: ${ids.length} ${plural(ids.length, ["группа", "группы", "групп"])}?`,
			message: archived
				? "Люди в группах останутся, а старые рассылки по ним сохранятся. В выборе групп они больше не покажутся. Вернуть можно во вкладке «Архив»."
				: "Группы снова появятся в списке и в выборе.",
			confirmText: archived ? "В архив" : "Вернуть",
			onConfirm: () => setArchived(ids, archived),
		})

	const empty = tags.length === 0
	const listId = `${view}-${empty ? (needle ? "none" : "empty") : "list"}`

	return (
		<section aria-labelledby="my-groups">
			<div className="mb-3 flex flex-wrap items-center gap-2">
				<Users className="size-4 text-primary" />
				<h2 id="my-groups" className="mr-auto text-base font-semibold tracking-tight">
					Свои группы
				</h2>
				<Button className="rounded-xl" onClick={() => setEditing({ name: "" })}>
					<Plus />
					Новая группа
				</Button>
			</div>

			<div className="mb-3 flex flex-wrap items-center gap-2">
				<SegmentedControl<View>
					aria-label="Какие группы показывать"
					value={view}
					onChange={(next) => {
						if (!next) return
						setView(next)
						setPicked(new Set())
					}}
					options={[
						{ value: "active", label: "Активные" },
						{ value: "archive", label: `Архив${archivedCount > 0 ? ` · ${archivedCount}` : ""}` },
					]}
				/>
				<div className="relative min-w-48 flex-1">
					<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						aria-label="Поиск по группам"
						placeholder="Найти группу"
						className="h-9 rounded-xl pl-9"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>
				</div>
				{view === "active" && snapshots.length > 0 ? (
					<Button
						variant="outline"
						className="h-9 rounded-xl"
						title="Отметит группы с датами и «Кто уйдёт» в названии: проверьте и уберите в архив"
						onClick={() => setPicked(new Set(snapshots.map((tag) => tag.id)))}
					>
						<Archive />
						Старые снимки · {snapshots.length}
					</Button>
				) : null}
			</div>

			<Reveal show={pickedIds.length > 0}>
				<div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card px-4 py-2.5">
					<span className="mr-auto text-sm">
						Отмечено: <span className="tabular-nums">{pickedIds.length}</span>
					</span>
					<Button
						variant="outline"
						className="h-9 rounded-xl"
						onClick={() => askArchive(pickedIds, view === "active")}
					>
						{view === "active" ? <Archive /> : <ArchiveRestore />}
						{view === "active" ? "В архив" : "Вернуть из архива"}
					</Button>
					<Button variant="ghost" className="h-9 rounded-xl" onClick={() => setPicked(new Set())}>
						Снять отметку
					</Button>
				</div>
			</Reveal>

			<LoadSwap loading={false} id={listId}>
				{empty ? (
					<EmptyState
						icon={view === "archive" ? Archive : Users}
						title={
							needle
								? "Ничего не нашлось"
								: view === "archive"
									? "В архиве пусто"
									: "Своих групп пока нет"
						}
						action={
							view === "active" && !needle ? (
								<Button className="rounded-xl" onClick={() => setEditing({ name: "" })}>
									<Plus />
									Создать группу
								</Button>
							) : undefined
						}
					>
						{needle
							? "Попробуйте другое слово."
							: view === "archive"
								? "Сюда попадают группы, которые убрали с глаз, но не удалили."
								: "Например, «горячие» для тех, кто спросил про цену."}
					</EmptyState>
				) : (
					<AutoHeight className="overflow-x-auto rounded-2xl border border-border bg-card">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-10">
										<Checkbox
											aria-label="Отметить все группы в списке"
											checked={allPicked}
											onCheckedChange={() =>
												setPicked(allPicked ? new Set() : new Set(tags.map((tag) => tag.id)))
											}
										/>
									</TableHead>
									<TableHead>Группа</TableHead>
									<TableHead className="w-28 text-right">Людей</TableHead>
									<TableHead className="w-44" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{tags.map((tag) => {
									const count = counts.get(tag.id) ?? 0
									const archived = !!tag.archivedAt
									return (
										<TableRow key={tag.id} data-state={picked.has(tag.id) ? "selected" : undefined}>
											<TableCell>
												<Checkbox
													aria-label={`Отметить ${tag.name}`}
													checked={picked.has(tag.id)}
													onCheckedChange={(on) => {
														const next = new Set(picked)
														if (on) next.add(tag.id)
														else next.delete(tag.id)
														setPicked(next)
													}}
												/>
											</TableCell>
											<TableCell className="font-medium">
												<Link
													to={botPath(botId, `analytics/groups/${tag.id}`)}
													className="link-underline"
													title="Открыть состав группы"
												>
													{tag.name}
												</Link>
											</TableCell>
											<TableCell className="text-right tabular-nums">
												<span className={count > 0 ? undefined : "text-muted-foreground"}>
													{count}
												</span>
											</TableCell>
											<TableCell>
												<div className="flex justify-end gap-1">
													<Button
														variant="ghost"
														size="icon"
														className="size-8"
														disabled={count === 0 || startBroadcast.isPending}
														title="Рассылка этой группе"
														aria-label={`Рассылка группе ${tag.name}`}
														onClick={() =>
															startBroadcast.mutate({
																name: `Группе «${tag.name}»`,
																filter: { tagId: tag.id },
															})
														}
													>
														<Megaphone />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="size-8"
														title="Переименовать"
														aria-label={`Переименовать ${tag.name}`}
														onClick={() => setEditing({ id: tag.id, name: tag.name })}
													>
														<Pencil />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="size-8"
														title={archived ? "Вернуть из архива" : "Убрать в архив"}
														aria-label={`${archived ? "Вернуть из архива" : "В архив"}: ${tag.name}`}
														onClick={() => void setArchived([tag.id], !archived)}
													>
														{archived ? <ArchiveRestore /> : <Archive />}
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="size-8 text-muted-foreground hover:text-red-300"
														title="Удалить"
														aria-label={`Удалить ${tag.name}`}
														onClick={() =>
															confirm({
																title: `Удалить группу «${tag.name}»?`,
																message: `${
																	count > 0
																		? `Сейчас в ней ${count} ${plural(count, PEOPLE)}. `
																		: ""
																}Сами люди останутся, пропадёт только группа. Блоки воронок, которые в неё добавляли, нужно будет поправить. Если группа просто не нужна на виду, лучше убрать её в архив.`,
																confirmText: "Удалить",
																onConfirm: async () => {
																	await actions.deleteTag(tag.id)
																},
															})
														}
													>
														<Trash2 />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</AutoHeight>
				)}
			</LoadSwap>

			<Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>{editing?.id ? "Переименовать группу" : "Новая группа"}</DialogTitle>
						<DialogDescription>
							{editing?.id
								? "Люди останутся в группе, поменяется только название."
								: "Людей в неё добавляют на странице группы, в карточке человека или кнопкой «Группа» в списке."}
						</DialogDescription>
					</DialogHeader>
					<form
						onSubmit={(event) => {
							event.preventDefault()
							void save()
						}}
						className="space-y-2"
					>
						<Input
							autoFocus
							aria-label="Название группы"
							placeholder="Например, горячие"
							maxLength={64}
							value={editing?.name ?? ""}
							onChange={(event) =>
								setEditing((current) =>
									current ? { ...current, name: event.target.value } : current,
								)
							}
						/>
						{duplicate ? <p className="text-xs text-amber-300">Такая группа уже есть</p> : null}
						<DialogFooter>
							<Button type="submit" disabled={!name || duplicate}>
								{editing?.id ? "Сохранить" : "Создать"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</section>
	)
}
